import { Logger } from '@gitbook/util-debug';
import { createCache } from '@gitbook/util-suspense-cache';

import { apiClient } from './api';
import { createLatestOrganizationSearch } from './latestOrganizationSearch';

export interface SearchRequest {
    organizationId: string;
    query: string;
}

const logger = Logger('search');
const CANCELED_SEARCH_RESULTS: ReadonlyArray<InAppSearchResult> = [];
const SEARCH_CACHE_TTL = 10000;
const LATEST_ORGANIZATION_SEARCH = createLatestOrganizationSearch({
    search: (organizationId: string, query: string, cancelToken: symbol) =>
        apiClient.organizations.searchOrganizationContent(
            organizationId,
            { query },
            { cancelToken }
        ),
    abort: (cancelToken: symbol) => apiClient.abortRequest(cancelToken),
});

export type InAppSearchResult =
    | {
          index: 'pages';
          object: {
              title: string;
              space: string;
              page: string;
              organization?: string;
          };
      }
    | {
          index: 'sections';
          object: {
              objectID: string;
              title: string;
              body: string;
              space: string;
              page: string;
              anchor: string;
              organization?: string;
          };
      };

/**
 * Suspense cache to access search results.
 */
export const searchCache = createCache<SearchRequest, ReadonlyArray<InAppSearchResult>>({
    // Clear results from API after 10 seconds to get up-to-date results
    ttl: (results) => (results === CANCELED_SEARCH_RESULTS ? -1 : SEARCH_CACHE_TTL),
    getValue: async ({ query, organizationId }) => {
        if (!query) {
            return [];
        }

        logger.debug('search', { query, organizationId });

        let response;
        try {
            response = await LATEST_ORGANIZATION_SEARCH.request(organizationId, query);
        } catch (error) {
            if (isAbortError(error)) {
                return CANCELED_SEARCH_RESULTS;
            }
            throw error;
        }

        const items = response.data?.items || [];
        const entries: InAppSearchResult[] = [];

        for (const spaceResult of items) {
            const spaceId = spaceResult.id;
            const pages = spaceResult.pages || [];

            for (const page of pages) {
                entries.push({
                    index: 'pages',
                    object: {
                        space: spaceId,
                        page: page.id,
                        organization: organizationId,
                        title: page.title,
                    },
                });

                // Map sections for this page, if any
                const sections = page.sections || [];
                for (const section of sections) {
                    // `anchor` isn't included in the API response, but it's available in the path.
                    // See services/cloudrun/api/src/operations/search/searchContent.ts#groupSectionsByPage
                    const hashIndex = section.path.indexOf('#');
                    const anchor = hashIndex !== -1 ? section.path.substring(hashIndex + 1) : '';

                    entries.push({
                        index: 'sections',
                        object: {
                            objectID: section.id,
                            space: spaceId,
                            page: page.id,
                            organization: organizationId,
                            title: section.title,
                            body: section.body,
                            anchor,
                        },
                    });
                }
            }
        }

        return entries;
    },
    getKey: ({ query, organizationId }) => `${organizationId}:${query}`,
});

/** Cancel the active organization-content search, if one is still running. */
export function cancelOrganizationSearch(organizationId: string): void {
    LATEST_ORGANIZATION_SEARCH.cancel(organizationId);
}

function isAbortError(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'AbortError'
    );
}
